import { createContext, useContext, useMemo, useState } from 'react'
import Cookies from 'js-cookie'

import { useUIVersion } from './UIVersionContext'

type DataLayoutContextType = {
  activeType: 'grid' | 'list'
  setActiveType: (type: 'grid' | 'list') => void
  isGridAvailable: boolean
}

const COOKIE_NAME = 'data_layout'

const DataLayoutContext = createContext<DataLayoutContextType | undefined>(undefined)
const DataLayoutScopeContext = createContext(false)

export function DataLayoutProvider({ children }: { children: React.ReactNode }) {
  const { isNewUI } = useUIVersion()
  const [type, setType] = useState<DataLayoutContextType['activeType']>(
    (Cookies.get(COOKIE_NAME) as DataLayoutContextType['activeType'] | undefined) ?? 'grid'
  )

  const setActiveType = (type: 'grid' | 'list') => {
    Cookies.set(COOKIE_NAME, type)
    setType(type)
  }

  const contextValue = useMemo(
    () => ({
      activeType: type,
      setActiveType,
      isGridAvailable: isNewUI,
    }),
    [isNewUI, type]
  )

  return <DataLayoutContext.Provider value={contextValue}>{children}</DataLayoutContext.Provider>
}

export function useDataLayout() {
  const context = useContext(DataLayoutContext)
  if (context === undefined) {
    throw new Error('useDataLayout must be used within DataLayoutProvider')
  }
  return context
}

export function DataLayoutScopeProvider({ children }: { children: React.ReactNode }) {
  return <DataLayoutScopeContext.Provider value={true}>{children}</DataLayoutScopeContext.Provider>
}

export function useResolvedDataLayout() {
  const { activeType, setActiveType, isGridAvailable } = useDataLayout()
  const isScoped = useContext(DataLayoutScopeContext)

  return {
    activeType: isScoped ? (isGridAvailable ? activeType : 'list') : 'grid',
    setActiveType,
    isGridAvailable: isScoped && isGridAvailable,
  }
}
